'use client';
import classNames from 'classnames';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import { useEffect, useId, useRef, useState } from 'react';
import { Autoplay, EffectFade, Navigation } from 'swiper';
import { isH5HandleClient } from 'eco-design-micro/utils-base/isH5Handle';
import { Swiper, SwiperSlide } from 'swiper/react';
import CardImgSecond from './img/CardImgSecond';

const VideoComp = ({ pc, h5 }) => {
    const [videoSrc, setVideoSrc] = useState(pc);
    useEffect(() => {
        if (isH5HandleClient()) {
            setVideoSrc(h5);
        } else {
            setVideoSrc(pc);
        }
    }, [pc, h5]);

    return <video playsInline muted autoPlay loop src={videoSrc}></video>;
};

interface Props {
    initIndex: number;
    autoPlay: boolean;
    list: any;
    prefixCls: string;
    className?: string;
    tabLayout: string;
}

export const BannerCom: React.FC<Props> = ({ initIndex, autoPlay, list, tabLayout, prefixCls, className, ...reset }) => {
    const swiperRef = useRef<any>(null);
    const [activeIndex, setActiveIndex] = useState(initIndex);
    const itemSlideChange = (swiperA: any) => {
        setActiveIndex(swiperA.realIndex);
    };

    const uuid = useId().replaceAll(':', 'a');

    const [isH5, setIsH5] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        setIsH5(isH5HandleClient());
    }, []);

    return (
        <Swiper
            className={`${className} ${className}-${uuid}`}
            onSlideChange={itemSlideChange}
            initialSlide={initIndex}
            pagination={false}
            navigation={{
                nextEl: `.${className}-${uuid} .swiper-button-next`,
                prevEl: `.${className}-${uuid} .swiper-button-prev`,
            }}
            loop={(list?.length ?? 0) > 1}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            speed={1000}
            allowTouchMove={isH5}
            autoplay={
                autoPlay
                    ? {
                          delay: 3000, // 轮播间隔时间（毫秒），默认3000
                          disableOnInteraction: false, // 用户操作后是否停止自动轮播，false表示不停止
                      }
                    : false
            }
            modules={[Navigation, Autoplay, EffectFade]}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            key={
                !isH5
                    ? autoPlay
                        ? `${className}-autoplay-on`
                        : `${className}-autoplay-off`
                    : autoPlay
                    ? `${className}-autoplay-on-h5`
                    : `${className}-autoplay-off-h5`
            }
        >
            {list.map((itemA: any, index: number) => {
                const { pc, h5, label, title, desc, theme = 'white', layout, lHref, lBtnContent, rHref, rBtnContent, isVideo } = itemA;
                const itemClassesA = classNames({
                    [`${prefixCls}-item`]: true,
                    [`${prefixCls}-item-${index}`]: true,
                    [`${prefixCls}-theme-${theme}`]: theme,
                });
                return (
                    <SwiperSlide className={itemClassesA} key={`${index}`}>
                        <div className={`${prefixCls}-item-img`}>
                            {!isVideo && (
                                <>
                                    {index === 0 && <EcoImg pc={pc} h5={h5} />}
                                    {index !== 0 && <CardImgSecond prefixCls={prefixCls} pc={pc} h5={h5} />}
                                </>
                            )}
                            {isVideo && <VideoComp pc={pc} h5={h5} />}
                        </div>
                        <div className={`${prefixCls}-item-msg`}>
                            <div className={`${prefixCls}-item-info ${layout}`}>
                                {label && <div className={`${prefixCls}-item-label`}>{label}</div>}
                                {title && <div className={`${prefixCls}-item-title`}>{title}</div>}
                                {desc && <div className={`${prefixCls}-item-desc`}>{desc}</div>}
                                <div className={`${prefixCls}-box-btn`}>
                                    <EcoButton lTheme={theme} lType="filled" lRadius="ellipse" lHref={lHref} lChildren={lBtnContent} />
                                    <EcoButton
                                        lTheme={theme}
                                        lType="outlined"
                                        lRadius="ellipse"
                                        lHref={rHref}
                                        lChildren={rBtnContent}
                                        lIsWithRightArrow={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                );
            })}
            {(list?.length ?? 0) > 1 && (
                <>
                    <div className={`${prefixCls}-control`}>
                        <div className={`swiper-button-prev`}></div>
                        <div className={`swiper-button-next`}></div>
                    </div>
                    <ul className={`${prefixCls}-pagination ${prefixCls}-pagination-${tabLayout}`}>
                        {list.map((itemB: any, index: number) => {
                            const itemClassesB = classNames({
                                [`${prefixCls}-pagination-item`]: true,
                                [`${prefixCls}-pagination-active`]: index === activeIndex,
                            });
                            return (
                                <li
                                    key={index}
                                    className={itemClassesB}
                                    onClick={() => {
                                        if (swiperRef.current) {
                                            swiperRef.current.slideTo(index + 1);
                                            setActiveIndex(index);
                                        }
                                    }}
                                >
                                    <div className={`${prefixCls}-pagination-title`}>{itemB.tab_name}</div>
                                    <div className={`${prefixCls}-pagination-line`}></div>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </Swiper>
    );
};
